// ---------------------------------------------------------------------------
// Manual translation (English -> Arabic). No auto-translate, no extra packages.
//
// HOW IT WORKS
//   Every English text in the app is wrapped like this:   tr("Sign in")
//   tr() looks the English text up in ar.js. If you wrote an Arabic value
//   there, the Arabic is shown. If the value is still empty "", the English
//   text is shown, so the app never breaks while you are still translating.
//
// PLACEHOLDERS  {0} {1} ...
//   Some texts contain live values, e.g.  tr("Logged in as: {0}", role)
//   Keep the {0} in your Arabic text where the value should appear:
//     "Logged in as: {0}": "تم تسجيل الدخول باسم: {0}",
//
// SWITCHING LANGUAGE
//   setLanguage('ar') / setLanguage('en') saves the choice in the browser and
//   reloads the page, so every page, alert and table header re-reads it.
//   The user stays logged in (the token lives in sessionStorage).
//
// ADDING NEW TEXT LATER
//   1. In your component:  import { tr } from '../i18n';   (fix the ../ depth)
//   2. Write  {tr("My new text")}  instead of  My new text
//   3. Add  "My new text": "النص الجديد",  to ar.js
//
// SAME ENGLISH, DIFFERENT MEANING
//   trc('litres', 'Amount') reads "litres::Amount" from ar.js (see trc below).
// ---------------------------------------------------------------------------

import ar from './ar';

const STORAGE_KEY = 'app_lang';
const DICTIONARIES = { ar };

function readLanguage() {
  try {
    const saved = window.localStorage.getItem(STORAGE_KEY);
    return DICTIONARIES[saved] ? saved : 'en';
  } catch (e) {
    return 'en';
  }
}

// Read once at start-up. Changing language reloads the page (see setLanguage).
export const lang = readLanguage();
export const isRTL = lang === 'ar';

export function tr(text, ...args) {
  let out = text;
  if (typeof text === 'string' && lang !== 'en') {
    const translated = DICTIONARIES[lang][text];
    if (typeof translated === 'string' && translated.trim() !== '') {
      out = translated;
    }
  }
  if (args.length && typeof out === 'string') {
    out = out.replace(/\{(\d+)\}/g, (match, i) =>
      Number(i) < args.length ? String(args[Number(i)]) : match
    );
  }
  return out;
}

// The same English word can need two different Arabic words, e.g. "Amount"
// is money on most pages (المبلغ) but litres on the Orders page (الكمية).
// trc('litres', 'Amount') looks up "litres::Amount" in ar.js first and falls
// back to the plain "Amount" entry (and to the English) if there is none.
export function trc(context, text, ...args) {
  const key = `${context}::${text}`;
  const dict = DICTIONARIES[lang];
  if (dict && typeof dict[key] === 'string' && dict[key].trim() !== '') {
    return tr(dict[key], ...args);
  }
  return tr(text, ...args);
}

export function setLanguage(code) {
  if (code === lang) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch (e) {
    /* storage blocked: stay in the current language */
  }
  window.location.reload();
}

// Called once from index.js: sets <html lang="ar" dir="rtl"> for Arabic.
export function applyDocumentLanguage() {
  const html = document.documentElement;
  html.setAttribute('lang', lang);
  html.setAttribute('dir', isRTL ? 'rtl' : 'ltr');
}
